import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  OnboardingCatalogDto,
  OnboardingStatusDto,
  OnboardingSubmitRequest,
  DiscoveryConfirmRequest,
  PaymentProcessorKind,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiVoid } from './apiFetch';
import { financeQueries, queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import { FINANCE_STALE_TIME } from './queryUtils';

/**
 * The single source of truth for "finance setup isn't finished yet". Setup is
 * incomplete when EITHER a bank OR a payment processor is still missing — the
 * `||` (not `&&`) is deliberate: the checklist framing keeps the nudge alive
 * until BOTH exist. `undefined` (status still loading) is treated as NOT
 * incomplete so a cold load never flashes a false "!". Both the Settings-tab
 * indicator (`FinanceLayout`) and the dashboard setup card (`FinanceDashboard`)
 * read this so the two surfaces can never drift out of sync.
 */
export function isOnboardingIncomplete(
  // Only the two counts drive the predicate — narrowed so callers (and tests)
  // don't have to synthesize the rest of the onboarding DTO.
  status: Pick<OnboardingStatusDto, 'bankCount' | 'processorCount'> | undefined,
): boolean {
  if (!status) return false;
  return status.bankCount === 0 || status.processorCount === 0;
}

// ============================================================================
// Query Hooks
// ============================================================================

export function useOnboardingCatalog() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.onboardingCatalog(),
    queryFn: () =>
      apiJson<OnboardingCatalogDto>('/api/finance/onboarding/catalog', {
        error: 'Failed to fetch onboarding catalog',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    // Catalog is global reference data — pre-shipped banks + processor enum.
    // Cache it for the session.
    staleTime: 60 * 60 * 1000,
  });
}

export function useOnboardingStatus() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.onboardingStatus(),
    queryFn: () =>
      apiJson<OnboardingStatusDto>('/api/finance/onboarding/status', {
        error: 'Failed to fetch onboarding status',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    staleTime: FINANCE_STALE_TIME,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useSubmitOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OnboardingSubmitRequest) =>
      apiJson<OnboardingStatusDto>('/api/finance/onboarding/submit', {
        method: 'POST',
        json: data,
        error: (body) => new Error(body.error || 'Failed to save onboarding'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(financeQueries);
    },
  });
}

export function useConfirmDiscovery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DiscoveryConfirmRequest) =>
      apiJson<OnboardingStatusDto>('/api/finance/onboarding/discover', {
        method: 'POST',
        json: data,
        error: (body) => new Error(body.error || 'Failed to confirm discovery'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(financeQueries);
    },
  });
}

// ============================================================================
// Removal Hooks (Banks & processors settings page)
//
// Unchecking a SAVED bank/processor removes it. Removal is hard-delete-when-empty
// on the backend; a bank/processor that still has imported data returns a 409
// with a treasurer-readable message. Both hooks surface that message as the
// thrown Error so the page can render it inline, and invalidate the onboarding
// status on settle so counts + checked state reconcile.
// ============================================================================

export function useRemoveOnboardingBank() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bankSourceProfileId: string) =>
      apiVoid(`/api/finance/onboarding/banks/${bankSourceProfileId}`, {
        method: 'DELETE',
        // The server message is already treasurer-safe (counts, no enum names).
        error: (body) => new Error(body.error || 'Failed to remove bank'),
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.onboardingStatus() });
    },
  });
}

export function useRemoveOnboardingProcessor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (kind: PaymentProcessorKind) =>
      apiVoid(`/api/finance/onboarding/processors/${kind}`, {
        method: 'DELETE',
        error: (body) => new Error(body.error || 'Failed to remove processor'),
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.onboardingStatus() });
    },
  });
}
